import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import type { Role } from "@avestate/shared";
import { supabase } from "@/lib/supabase.js";

export type AuthState = {
  session: Session | null;
  role: Role | null;
  org_id: string | null;
  email: string | null;
  ready: boolean;          // true once we've attempted initial restore
};

type AuthApi = AuthState & {
  signInWithMagicLink: (email: string) => Promise<{ ok: boolean; error?: string }>;
  signInWithTotp: (code: string) => Promise<{ ok: boolean; error?: string }>;
  setSessionFromTokens: (access_token: string, refresh_token: string) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => Promise<void>;
};

export const AuthContext = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({
    session: null, role: null, org_id: null, email: null, ready: false,
  });
  const sb = useRef(supabase());

  // Fetch /api/v1/me to resolve org_id + role for the given access token.
  const resolveMe = useCallback(async (access_token: string): Promise<{ role: Role; org_id: string; email: string } | null> => {
    const apiBase = import.meta.env.VITE_API_BASE_URL as string;
    const res = await fetch(`${apiBase}/api/v1/me`, {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    if (!res.ok) return null;
    const body = await res.json() as { role: Role; org_id: string; email: string };
    return body;
  }, []);

  const setSessionFromTokens = useCallback(async (access_token: string, refresh_token: string) => {
    const { data, error } = await sb.current.auth.setSession({ access_token, refresh_token });
    if (error || !data.session) return { ok: false, error: error?.message ?? "setSession returned null" };
    const me = await resolveMe(data.session.access_token);
    if (!me) return { ok: false, error: "Failed to resolve membership" };
    setState({
      session: data.session,
      role: me.role,
      org_id: me.org_id,
      email: me.email,
      ready: true,
    });
    return { ok: true };
  }, [resolveMe]);

  const signInWithTotp = useCallback(async (code: string) => {
    const apiBase = import.meta.env.VITE_API_BASE_URL as string;
    let body: { access_token?: string; refresh_token?: string; error?: { message?: string } };
    try {
      const res = await fetch(`${apiBase}/api/v1/auth/totp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.replace(/\s/g, "") }),
      });
      body = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: body?.error?.message ?? "Codice non valido" };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
    if (!body.access_token || !body.refresh_token) return { ok: false, error: "Risposta non valida" };
    return setSessionFromTokens(body.access_token, body.refresh_token);
  }, [setSessionFromTokens]);

  const signInWithMagicLink = useCallback(async (email: string) => {
    const { error } = await sb.current.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  }, []);

  const signOut = useCallback(async () => {
    await sb.current.auth.signOut().catch(() => {});
    setState({ session: null, role: null, org_id: null, email: null, ready: true });
  }, []);

  // On mount: nothing to restore (persistSession: false). Mark ready.
  useEffect(() => {
    setState((s) => ({ ...s, ready: true }));
  }, []);

  const api = useMemo<AuthApi>(() => ({
    ...state,
    signInWithMagicLink,
    signInWithTotp,
    setSessionFromTokens,
    signOut,
  }), [state, signInWithMagicLink, signInWithTotp, setSessionFromTokens, signOut]);

  return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>;
}
